import { useRef, useState } from 'react'

import { Form, Formik } from 'formik'
import * as yup from 'yup'

import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    makeStyles,
    TextField,
    Typography
} from '@material-ui/core'
import AddBoxIcon from '@material-ui/icons/AddBox'
import { getInviteDefaults, ConnectedInput } from 'UI/Components'

import { useCustomersDomains } from './use-customers-domains'

import { ConfirmOutsideDialog } from './ConfirmOutsideDialog'

import { updateSpace, logToAnalytics, SPACE_ROLES, option, copyToClipboard } from 'modules'

import {
    useAuth,
    useBoolean,
    useDataMutation,
    useNotification,
    useOrganization,
    useRoles,
    useOrgFeatureFlags,
    useOrganizationUsers
} from 'hooks'

const useStyles = makeStyles(({ spacing, typography }) => ({
    title: {
        fontSize: '1.5rem',
        textAlign: 'center',
        fontWeight: typography.fontWeightBold
    },
    actions: {
        padding: spacing(3),
        justifyContent: 'center'
    },
    fieldTitle: {
        fontSize: typography.pxToRem(14)
    },
    counter: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        marginRight: spacing(2),
        marginBottom: spacing(1),
        color: '#78909c',
        fontWeight: 500
    },
    shareTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        '& button': {
            padding: 0,

            '&, &:hover': {
                background: 'transparent',
                textDecoration: 'underline'
            }
        }
    },
    copyLinkBox: {
        display: 'flex',
        alignItems: 'flex-end',

        '& input': {
            fontSize: typography.pxToRem(14)
        },

        '& button': {
            marginLeft: spacing(2)
        }
    },
    addInputButton: {
        padding: spacing(1),
        margin: -spacing(1),
        minWidth: 'auto',

        '&, &:hover': {
            boxShadow: 'none',
            background: 'transparent'
        }
    },
    formSection: {
        background: '#fff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px'
    },
    shareLinkBox: {
        marginTop: spacing(2),
        padding: spacing(2, 3),
        background: '#fff',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px'
    }
}))

export const InviteToSpaceForm = ({ onClose, space }) => {
    const classes = useStyles()

    const formikRef = useRef()

    const { domains: customersDomains } = useCustomersDomains()
    const { domains: orgDomains, name: orgName } = useOrganization()
    const { orgUsers } = useOrganizationUsers()

    const { uid, isPrivateAccount } = useAuth()
    const { isAdmin, isContentManager, isViewer } = useRoles(space?.organizationId)

    const [emailsInputsQuantity, setEmailsInputsQuantity] = useState(4)
    const { showSuccessNotification, showErrorNotification } = useNotification()

    const { customers } = useCustomersDomains()

    const domains = orgDomains.join(', ')

    const { disableExternalSharing } = useOrgFeatureFlags()

    const $inviteToSpace = useDataMutation('/b/v1/userInvite', 'POST', {
        onSuccess: () => {
            showSuccessNotification('Invitation has been sent')
        }
    })

    const showConfirmationDialog = useBoolean()

    const addSpacePartners = (emails, customers) => {
        // Check each email's domain. If current domain belongs to customer's domain
        // - we should add info (id, name, domains) of customer to the space.spacePartners property
        const spacePartnersEntries = emails
            .map(email => {
                const domain = email.value.split('@')[1]

                // Current domain - is our domain. No action requires
                if (orgDomains.includes(domain)) {
                    return
                }

                // Current domain - is not of one of our customers. This handle backend
                if (!customersDomains.includes(domain)) {
                    return
                }

                // get customer who contains current email domain
                const customer = Object.values(customers).find(customer =>
                    customer.domains.includes(domain)
                )

                if (!customer?.id) return

                // check whether this customer is already added to the spacePartners property
                if (space.spacePartners?.[customer.id] !== undefined) {
                    return
                }

                const { id, name, domains } = customer

                return [id, { id, name, domains }]
            })
            .filter(Boolean)

        if (!spacePartnersEntries.length) {
            return
        }

        const spacePartners = Object.fromEntries(spacePartnersEntries)

        return updateSpace(space.id, {
            spacePartners: {
                ...(space.spacePartners || {}),
                ...spacePartners
            }
        })
    }

    const handleInvite = async () => {
        const refValues = formikRef?.current?.values || {}
        const allEmailKeys = Object.keys(refValues).filter(
            val => val.includes('email-') && Boolean(refValues[val])
        )
        const email = allEmailKeys.map(eKey =>
            option(
                refValues[eKey],
                Object.values(orgUsers).find(oUser => oUser.email === refValues[eKey])?.displayName
            )
        )
        const emails = allEmailKeys.map(eKey => refValues[eKey]).join(',')
        // const emails = email?.map(a => a.value).join(',')
        // Before send an api call, we need to check each email's domain for use cases:
        // 1) domain belongs to our organization - no additional actions required
        // 2) domain belongs to our customer - add info (id, name, domains) of customer to the space.spacePartners property

        await addSpacePartners(email, customers)

        // Based on current user's role (roles.a property) inside this space he can set different roles to the new user during invite
        // If current user is OWNER (a=60) or COLLABORATOR (a=40) new user can be COLLABORATOR or OWNER (default collaborator)
        // If current user is VIEWER (a=20) he can only invite new viewer

        const spacePermission = SPACE_ROLES.VIEWER

        return $inviteToSpace
            .mutate({
                emails,
                message: refValues.message,
                spaceId: space.id,
                spacePermission
            })
            .finally(onClose)
            .then(() =>
                logToAnalytics('send_invite_to_space', {
                    'space-name': space.name,
                    'space-id': space.id,
                    emails
                })
            )
            .catch(e => {
                const message =
                    typeof e === 'string' ? e : 'Something went wrong. Please try again later'
                showErrorNotification(message)
                return false
            })
    }

    const validateEmail = (email, createError, ind) => {
        if (disableExternalSharing) {
            if (email?.includes('@')) {
                const parts = email.split('@')
                if (!orgDomains?.includes(parts[1])) {
                    return createError({
                        path: `email-${ind}`,
                        message: `Allowed domains: ${domains}`
                    })
                }
            }
        }
        return true
    }

    const defaultValuesForEmails = {}
    const validations_data = {}
    const email_validation = yup.string().trim().email('Please provide correct emails.')

    Array(emailsInputsQuantity)
        .fill(true)
        .forEach((elem, ind) => {
            validations_data[`email-${ind}`] =
                ind === 0
                    ? email_validation
                          .required('Field is required')
                          .min(1, 'Field is required')
                          .trim()
                          .test('Is domain blacklisted', '', (email, { createError }) =>
                              validateEmail(email, createError, ind)
                          )
                    : email_validation
                          .nullable(true)
                          .test('Is domain blacklisted', '', (email, { createError }) =>
                              validateEmail(email, createError, ind)
                          )
            defaultValuesForEmails[`email-${ind}`] = ''
        })

    const handleAddInputField = () => {
        setEmailsInputsQuantity(pre => (pre += 1))
    }

    const { isLoading } = $inviteToSpace

    const $toggleInviteLink = useDataMutation('/h/v1/toggle-space-invite', 'POST', {
        onSuccess: ({ enabled }) => {
            showSuccessNotification(`The invite link has been ${enabled ? 'enabled' : 'disabled'}`)
        }
    })

    if (!orgName) return <LinearProgress />

    const toggleSpaceInvite = () => {
        $toggleInviteLink.mutate({
            spaceId: space.id,
            shareLinkId: space.shareLinkId,
            orgId: space.organizationId
        })
    }

    const shareLink = `${location.origin}/join-space/${space.id}/${space.shareLinkId}?origin=${uid}&referral=invite&invited_email=`
    const isOwner = space.members?.[uid]?.roles?.a === SPACE_ROLES.OWNER
    /* Admin and content manager can see and enable/disable link despite space role
     * Member in org can only see link if it is enabled
     * Space owner can see and enable/disable link if he isn't viewer in org
     */
    const canSeeShareLink =
        isAdmin || isContentManager || ((isOwner || space.shareLinkId) && !isViewer)

    const inviteDefaults = getInviteDefaults(orgName, isPrivateAccount)

    return (
        <>
            {isLoading && showConfirmationDialog.isFalse && <LinearProgress />}
            <Formik
                innerRef={formikRef}
                onSubmit={values => {
                    const emailsList = Object.keys(values).filter(
                        val => val.includes('email-') && Boolean(values[val])
                    )
                    const email = []
                    emailsList.forEach(em => email.push(values[em].trim()))
                    const forbiddenDomains = email.filter(
                        email => !orgDomains.includes(email.split('@')[1])
                    )
                    if (!forbiddenDomains.length) {
                        return handleInvite()
                    }

                    // An invite to a space for an outside domain could be only done by a space owner or space admin or content manager
                    if (
                        space.members?.[uid]?.roles?.a !== SPACE_ROLES.OWNER &&
                        !isAdmin &&
                        !isContentManager
                    ) {
                        const error = isPrivateAccount
                            ? `You don't have permissions to invite users in this space`
                            : `You don't have permission to invite user outside of your organization. Forbidden domains: ${forbiddenDomains
                                  .map(value => value)
                                  .join(', ')}`

                        return showErrorNotification(error)
                    }

                    if (isPrivateAccount) {
                        handleInvite()
                    } else {
                        showConfirmationDialog.setTrue()
                    }
                }}
                initialValues={{
                    ...defaultValuesForEmails,
                    message: inviteDefaults.message
                }}
                validationSchema={yup.object().shape({
                    ...validations_data
                })}
            >
                {({ values: { message } }) => (
                    <Form className={classes.formSection}>
                        <DialogTitle>
                            <Typography className={classes.title}>Invite to space</Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box mb={2}>
                                <Typography variant="body1">
                                    People you invite will receive an email with an invitation to
                                    join <b>{space?.name}</b>
                                </Typography>
                            </Box>
                            <Box mb={2}>
                                <Typography className={classes.fieldTitle}>
                                    <b>Email address(s)</b>
                                </Typography>
                                <Box mt={1}>
                                    {[...Array(emailsInputsQuantity)].map((_, i) => (
                                        <Box key={i} mb={2}>
                                            <ConnectedInput
                                                data-cy="space-email-input"
                                                variant="outlined"
                                                name={`email-${i}`}
                                                placeholder="john@acme.com"
                                                fullWidth
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                                <Button
                                    className={classes.addInputButton}
                                    title="Add one more email"
                                    onClick={handleAddInputField}
                                    size="small"
                                    color="default"
                                    disableRipple
                                >
                                    <AddBoxIcon color="action" />
                                </Button>
                            </Box>
                            <Box mb={2}>
                                <Typography className={classes.fieldTitle}>
                                    <b>Message</b>
                                </Typography>

                                <Box mt={1} position="relative">
                                    <ConnectedInput
                                        name="message"
                                        placeholder="Enter the message"
                                        multiline
                                        variant="outlined"
                                        fullWidth
                                        inputProps={{
                                            maxLength: 1500
                                        }}
                                    />
                                    <Typography className={classes.counter}>
                                        {message.length}/{inviteDefaults.maxLength}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions className={classes.actions}>
                            <Button size="medium" color="default" type="button" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                data-cy="space-invite-button"
                                size="medium"
                                type="submit"
                                disabled={isLoading}
                            >
                                Invite
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
            {canSeeShareLink && !disableExternalSharing && (
                <Box className={classes.shareLinkBox}>
                    <Box className={classes.shareTitleWrapper}>
                        <Typography className={classes.fieldTitle}>
                            <b>Invite with link</b>
                        </Typography>
                        {(isAdmin || isContentManager || isOwner) && (
                            <Button
                                variant="text"
                                onClick={toggleSpaceInvite}
                                disabled={$toggleInviteLink.isLoading}
                                color="default"
                            >
                                {space.shareLinkId ? `Disable link` : `Enable link`}
                            </Button>
                        )}
                    </Box>
                    <Typography className={classes.fieldTitle}>
                        Anyone with this link can join as viewer
                    </Typography>

                    {space.shareLinkId && (
                        <Box className={classes.copyLinkBox}>
                            <TextField
                                type="text"
                                value={shareLink}
                                readOnly
                                style={{ flexGrow: 1 }}
                            />
                            <Button
                                onClick={() =>
                                    copyToClipboard(shareLink).then(() =>
                                        showSuccessNotification('Link copied to the clipboard')
                                    )
                                }
                                color="default"
                            >
                                Copy
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
            <ConfirmOutsideDialog
                isOpen={showConfirmationDialog.isTrue}
                formikValues={formikRef?.current?.values}
                onClose={showConfirmationDialog.setFalse}
                onConfirm={handleInvite}
            />
        </>
    )
}
